import React, { useState, useEffect } from 'react';
import { 
  Package, Calendar, ExternalLink, Check, MessageSquare, 
  Loader2, Clock, CheckCircle, AlertTriangle, Image, Film, 
  Layers, MoreHorizontal, ChevronDown, ChevronUp, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_review: { label: 'Pendiente de revisión', color: 'bg-[#E86A1F]', textColor: 'text-[#E86A1F]' },
  approved: { label: 'Aprobada', color: 'bg-green-500', textColor: 'text-green-400' },
  adjustment: { label: 'Ajuste solicitado', color: 'bg-red-500', textColor: 'text-red-400' },
  corrected: { label: 'Corregida', color: 'bg-blue-500', textColor: 'text-blue-400' },
  published: { label: 'Publicada', color: 'bg-emerald-600', textColor: 'text-emerald-400' }
};

const FORMAT_CONFIG = {
  post: { label: 'Post', icon: Image },
  historia: { label: 'Historia', icon: Clock },
  reel: { label: 'Reel', icon: Film },
  carrusel: { label: 'Carrusel', icon: Layers },
  otro: { label: 'Otro', icon: MoreHorizontal }
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const days = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
}

// Adjustment Modal
function AdjustmentModal({ show, onClose, onSubmit }) {
  const [comment, setComment] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error('Escribe un comentario');
      return;
    }
    onSubmit(comment);
    setComment('');
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white/70 rounded-2xl max-w-md w-full p-5">
        <h3 className="text-lg font-bold text-[#1D1D1F] mb-2">Solicitar Ajuste</h3>
        <p className="text-sm text-[#AEAEB2] mb-4">
          Describe brevemente qué cambio necesitas. Tu comentario será enviado al equipo.
        </p>
        <form onSubmit={handleSubmit}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ej: El logo aparece muy pequeño, ¿pueden hacerlo más grande?"
            rows={3}
            className="w-full px-4 py-3 bg-gray-700 border border-[rgba(0,0,0,0.08)] rounded-xl text-[#1D1D1F] placeholder-[#AEAEB2] text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            autoFocus
          />
          <div className="flex gap-3 mt-4">
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-xl font-bold"
            >
              Enviar Comentario
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 bg-gray-700 text-[#6E6E73] rounded-xl font-semibold"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Single Piece Card for Client Review
function PieceReviewCard({ piece, onApprove, onRequestAdjustment }) {
  const [loading, setLoading] = useState(false);
  const status = STATUS_CONFIG[piece.status] || STATUS_CONFIG.pending_review;
  const format = FORMAT_CONFIG[piece.format] || FORMAT_CONFIG.otro;
  const FormatIcon = format.icon;

  const isPending = ['pending_review', 'corrected'].includes(piece.status);

  const handleApprove = async () => {
    setLoading(true);
    await onApprove(piece.piece_id);
    setLoading(false);
  };

  return (
    <div className={`bg-white/50 rounded-xl border ${isPending ? 'border-orange-500/30' : 'border-[rgba(0,0,0,0.06)]/50'} overflow-hidden`}>
      {/* Header */}
      <div className="p-4 border-b border-[rgba(0,0,0,0.06)]/50">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-[#1D1D1F] ${status.color}`}>
              {piece.status === 'corrected' && <Send className="w-3 h-3" />}
              {status.label}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-700 text-[#6E6E73] rounded-full text-xs">
              <FormatIcon className="w-3 h-3" />
              {format.label}
            </span>
          </div>
          <span className="text-xs text-[#AEAEB2] flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(piece.scheduled_date)}
          </span>
        </div>
        <p className="text-[#1D1D1F] text-sm">{piece.description}</p>
      </div>

      {/* Drive Link */}
      <a
        href={piece.drive_link}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 py-3 bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors text-sm font-medium"
      >
        <ExternalLink className="w-4 h-4" />
        Ver Archivo en Drive
      </a>

      {/* Actions - Only for pending pieces */}
      {isPending && (
        <div className="p-3 bg-white/40 flex gap-2">
          <button
            onClick={handleApprove}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold text-sm hover:shadow-lg transition disabled:opacity-50"
            data-testid={`approve-btn-${piece.piece_id}`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Aprobar
          </button>
          <button
            onClick={() => onRequestAdjustment(piece)}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-700 text-[#6E6E73] rounded-xl font-bold text-sm hover:bg-white/60 transition"
            data-testid={`adjust-btn-${piece.piece_id}`}
          >
            <MessageSquare className="w-4 h-4" />
            Solicitar Ajuste
          </button>
        </div>
      )}

      {/* Previous adjustment comment */}
      {piece.adjustment_comment && piece.status !== 'adjustment' && (
        <div className="p-3 bg-amber-900/20 border-t border-[#E86A1F]/20">
          <p className="text-xs text-[#E86A1F] mb-1 font-medium">Tu comentario anterior:</p>
          <p className="text-xs text-[#AEAEB2] italic">"{piece.adjustment_comment}"</p>
        </div>
      )}
    </div>
  );
}

// Week Group Component
function WeekGroup({ weekLabel, pieces, onApprove, onRequestAdjustment }) {
  const [expanded, setExpanded] = useState(true);
  const pendingCount = pieces.filter(p => ['pending_review', 'corrected'].includes(p.status)).length;

  return (
    <div className="bg-white/70/30 rounded-xl border border-[rgba(0,0,0,0.06)]/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/50 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-orange-400" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-[#1D1D1F]">{weekLabel}</h4>
            <p className="text-xs text-[#AEAEB2]">{pieces.length} pieza{pieces.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
              {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-[#AEAEB2]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#AEAEB2]" />
          )}
        </div>
      </button>
      
      {expanded && (
        <div className="p-3 pt-0 space-y-3">
          {pieces.map(piece => (
            <PieceReviewCard
              key={piece.piece_id}
              piece={piece}
              onApprove={onApprove}
              onRequestAdjustment={onRequestAdjustment}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Main Component
export default function ContentReviewTab() {
  const [pieces, setPieces] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [adjustmentModal, setAdjustmentModal] = useState({ show: false, piece: null });

  useEffect(() => {
    loadPieces();
  }, []);

  const loadPieces = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(
        `${API_URL}/api/content-pieces/my-all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPieces(response.data.pieces || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error loading pieces:', error);
      toast.error('Error al cargar contenido');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (pieceId) => {
    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(
        `${API_URL}/api/content-pieces/my/${pieceId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('¡Pieza aprobada!');
      loadPieces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    }
  };

  const handleRequestAdjustment = (piece) => {
    setAdjustmentModal({ show: true, piece });
  };

  const submitAdjustment = async (comment) => {
    const piece = adjustmentModal.piece;
    if (!piece) return;

    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(
        `${API_URL}/api/content-pieces/my/${piece.piece_id}/request-adjustment`,
        { comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ajuste solicitado');
      setAdjustmentModal({ show: false, piece: null });
      loadPieces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar ajuste');
    }
  };

  // Filter pieces
  const filteredPieces = pieces.filter(p => {
    if (filter === 'pending') return ['pending_review', 'corrected'].includes(p.status);
    if (filter === 'approved') return p.status === 'approved';
    if (filter === 'adjustment') return p.status === 'adjustment';
    if (filter === 'published') return p.status === 'published';
    return true;
  });

  // Group by week_label
  const groupedByWeek = {};
  const ungrouped = [];

  filteredPieces.forEach(p => {
    if (p.week_label) {
      if (!groupedByWeek[p.week_label]) groupedByWeek[p.week_label] = [];
      groupedByWeek[p.week_label].push(p);
    } else {
      ungrouped.push(p);
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const pendingCount = stats.pending || 0;

  return (
    <div className="space-y-5" data-testid="content-review-tab">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl">
          <Package className="w-6 h-6 text-[#1D1D1F]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#1D1D1F]">Revisión de Contenido</h2>
          <p className="text-sm text-[#AEAEB2]">Aprueba o solicita ajustes en tu contenido</p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-2">
        <div className={`rounded-lg p-3 text-center ${pendingCount > 0 ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-white/50'}`}>
          <p className={`text-xl font-bold ${pendingCount > 0 ? 'text-orange-400' : 'text-[#AEAEB2]'}`}>{pendingCount}</p>
          <p className="text-xs text-[#AEAEB2]">Pendientes</p>
        </div>
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-[#E86A1F]">{stats.adjustments || 0}</p>
          <p className="text-xs text-[#AEAEB2]">En ajuste</p>
        </div>
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-green-400">{stats.approved || 0}</p>
          <p className="text-xs text-[#AEAEB2]">Aprobadas</p>
        </div>
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-emerald-400">{stats.published || 0}</p>
          <p className="text-xs text-[#AEAEB2]">Publicadas</p>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && filter !== 'pending' && (
        <div 
          onClick={() => setFilter('pending')}
          className="p-4 bg-orange-500/20 border border-orange-500/30 rounded-xl flex items-center gap-3 cursor-pointer hover:bg-orange-500/30 transition"
        >
          <AlertTriangle className="w-5 h-5 text-orange-400" />
          <p className="text-sm text-orange-300">
            Tienes <strong>{pendingCount} pieza{pendingCount !== 1 ? 's' : ''}</strong> pendiente{pendingCount !== 1 ? 's' : ''} de revisión
          </p>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { key: 'pending', label: 'Pendientes', count: stats.pending },
          { key: 'adjustment', label: 'En ajuste', count: stats.adjustments },
          { key: 'approved', label: 'Aprobadas', count: stats.approved },
          { key: 'published', label: 'Publicadas', count: stats.published },
          { key: 'all', label: 'Todas' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              filter === f.key 
                ? 'bg-orange-500 text-white' 
                : 'bg-white/70 text-[#AEAEB2] hover:bg-white/70'
            }`}
          >
            {f.label}
            {f.count > 0 && f.key !== 'all' && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs ${
                filter === f.key ? 'bg-white/20' : 'bg-gray-700'
              }`}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {filteredPieces.length === 0 ? (
        <div className="text-center py-12 bg-white/70/30 rounded-xl border border-[rgba(0,0,0,0.06)]/50">
          <Package className="w-12 h-12 text-[#6E6E73] mx-auto mb-3" />
          <p className="text-[#AEAEB2]">
            {filter === 'pending' 
              ? 'No tienes contenido pendiente de revisión' 
              : 'No hay contenido en esta categoría'}
          </p>
          {filter === 'pending' && (
            <p className="text-sm text-[#AEAEB2] mt-1">
              ¡Excelente! Todo está al día
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Grouped by week */}
          {Object.entries(groupedByWeek).map(([weekLabel, weekPieces]) => (
            <WeekGroup
              key={weekLabel}
              weekLabel={weekLabel}
              pieces={weekPieces}
              onApprove={handleApprove}
              onRequestAdjustment={handleRequestAdjustment}
            />
          ))}

          {/* Ungrouped pieces */}
          {ungrouped.length > 0 && (
            <div className="space-y-3">
              {Object.keys(groupedByWeek).length > 0 && (
                <h4 className="text-sm font-medium text-[#AEAEB2] px-1">Piezas individuales</h4>
              )}
              {ungrouped.map(piece => (
                <PieceReviewCard
                  key={piece.piece_id}
                  piece={piece}
                  onApprove={handleApprove}
                  onRequestAdjustment={handleRequestAdjustment}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Adjustment Modal */}
      <AdjustmentModal
        show={adjustmentModal.show}
        onClose={() => setAdjustmentModal({ show: false, piece: null })}
        onSubmit={submitAdjustment}
      />
    </div>
  );
}
